import { formatCurrency } from "utils/currency";
import {
  DiscountApplicationType,
  DiscountType,
} from "../../../__generated__/globalTypes";
import { YEARLY_BILLING_INTERVAL } from "../insurance";
import { getWebTranslation } from "translations";

export type AmountToPay = {
  totalSavings?: number;
  discountedPrice?: {
    amount: number;
    months: number;
  };
  old?: number;
  new: number;
  discounts: Array<{
    name: string;
    type: DiscountType;
    total: number;
    maxInvoicesSpread: number;
    applicationTotal: number;
    applicationType: DiscountApplicationType;
  }>;
};

export const getFormattedDiscountString = (
  amountToPay?: Omit<AmountToPay, "discounts"> | null
) => {
  if (!amountToPay || !amountToPay.discountedPrice) {
    return null;
  }

  if (amountToPay.discountedPrice.months === 12) {
    return null;
  }

  if (amountToPay.discountedPrice.months === 1) {
    return getWebTranslation("insurance.price.discount.first_month", {
      amount: formatCurrency(amountToPay.discountedPrice.amount),
    });
  }

  return getWebTranslation("insurance.price.discount.n_months", {
    amount: formatCurrency(amountToPay.discountedPrice.amount),
    months: amountToPay.discountedPrice.months,
  });
};

type AmountToPayInput = {
  /**
   * The amount to pay AFTER discounts
   */
  total?: number;
  /**
   * The amount to pay BEFORE discounts
   */
  netPremium?: number;
  /**
   * Max invoices spread is the number of months the discount applies to
   */
  discounts?:
    | {
        name: string;
        total: number;
        type: DiscountType;
        maxInvoicesSpread?: number | null;
        applicationTotal: number;
        applicationType: DiscountApplicationType;
      }[]
    | null;
  /**
   * Monthly or yearly billing interval
   */
  billingInterval: number | null;
};

/**
 * Tech debt: The `total`, `netPremium`, `fees` and `billingInterval` should
 * ideally not have default values here but if they don't, currently the resume
 * QP feature crashes. It's also the reason a quote viewed in the wrong country
 * costs 0 (happens in the local dev environment)
 */
export const getAmountToPay = ({
  total = 0,
  netPremium = 0,
  discounts = [],
  billingInterval = 1,
}: AmountToPayInput): AmountToPay => {
  const safeBillingInterval = billingInterval ?? 1;

  const completeDiscounts = (discounts ?? []).map((discount) => ({
    ...discount,
    maxInvoicesSpread: discount.maxInvoicesSpread ?? 12,
  }));

  const premiumBeforeDiscounts =
    Math.round((netPremium / 12) * safeBillingInterval) / 100;

  if (!discounts?.length) {
    return {
      new: premiumBeforeDiscounts,
      discounts: completeDiscounts,
    };
  }

  if (safeBillingInterval === YEARLY_BILLING_INTERVAL) {
    return {
      new: (total / 12 / 100) * safeBillingInterval,
      old: premiumBeforeDiscounts,
      discounts: completeDiscounts,
      totalSavings: (netPremium - total) / 100,
    };
  }

  const monthlyDiscount = completeDiscounts
    .filter(
      (discount) =>
        (discount.applicationType === DiscountApplicationType.percent &&
          discount.applicationTotal !== 1) ||
        discount.applicationType === DiscountApplicationType.flat
    )
    .reduce((acc, discount) => {
      return acc + Math.round(discount.total / discount.maxInvoicesSpread);
    }, 0);

  const monthlyDiscountedPrice =
    Math.round((netPremium / 12 - monthlyDiscount) * safeBillingInterval) / 100;

  const newPrice = Math.max(monthlyDiscountedPrice, 0);

  return {
    ...(total !== netPremium
      ? { totalSavings: (netPremium - total) / 100 }
      : {}),
    new: newPrice,
    ...(premiumBeforeDiscounts !== newPrice
      ? { old: premiumBeforeDiscounts }
      : {}),
    discounts: completeDiscounts,
  };
};
